import { gql } from '@apollo/client'
import { getWebClient } from '@/services/ApolloClient'
import { GetContinueWatchingQuery, ResumeWatchingQuery } from '@/types/codegen-federation'
import { isDefined, DeepNonNullable } from '@/utils/types'

export const GET_CONTINUE_WATCHING_DATA = gql`
  query getContinueWatching {
    projects {
      name
      slug
      logoCloudinaryPath
      projectType
      continueWatching {
        name
        subtitle
        id
        guid
        duration
        episodeNumber
        projectSlug
        posterLandscapeCloudinaryPath
        prereleaseAvailableFor
        userHasEarlyAccess
        season {
          id
          seasonNumber
        }
        watchPosition {
          id
          position
          updatedAt
        }
        earlyAccessDate
        publiclyAvailableDate
        isAngelGuildOnly
        prereleaseAvailableFor
        isTrailer
        guildAvailableDate
        publiclyAvailableDate
        earlyAccessDate
        unavailableReason
      }
    }
  }
`

export const GET_RESUME_WATCHING = gql`
  query ResumeWatching($first: Int, $after: String) {
    resumeWatching(first: $first, after: $after) {
      edges {
        node {
          watchableGuid
          position
          updatedAt
          content {
            __typename
            ... on ContentEpisode {
              id
              project {
                slug
                projectType
                name
              }
              name
              duration
              episodeNumber
              watchableAvailabilityStatus
              actionsToWatch {
                __typename
              }
              season {
                id
                seasonNumber
              }
            }
            ... on ContentSpecial {
              id
              project {
                slug
                projectType
                name
              }
              name
              duration
              watchableAvailabilityStatus
              actionsToWatch {
                __typename
              }
            }
            ... on ContentMovie {
              id
              name
              duration
              watchableAvailabilityStatus
              actionsToWatch {
                __typename
              }
              project {
                slug
                projectType
                name
              }
            }
            ... on ContentDisplayable {
              id
              name
              description(version: SHORT)
              landscapeNonTitleImage: image(aspect: "16:9", category: NON_TITLE_ART) {
                cloudinaryPath
              }
              landscapeStillImage: image(aspect: "16:9", category: STILL_1) {
                cloudinaryPath
              }
            }
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`

type ResumeWatchingEdgeRaw = DeepNonNullable<ResumeWatchingQuery['resumeWatching'], 2>['edges'][number]

type ResumeWatchingContent = Extract<
  NonNullable<ResumeWatchingEdgeRaw['node']['content']>,
  { __typename: 'ContentEpisode' | 'ContentMovie' | 'ContentSpecial' }
>

export type ResumeWatchingEdge = Omit<ResumeWatchingEdgeRaw, 'node'> & {
  node: Omit<ResumeWatchingEdgeRaw['node'], 'content'> & { content: ResumeWatchingContent }
}

export type ContinueWatchingProject = NonNullable<
  NonNullable<NonNullable<GetContinueWatchingQuery>['projects']>[number]
>

export type ContinueWatchingEpisode = NonNullable<
  NonNullable<NonNullable<NonNullable<GetContinueWatchingQuery>['projects']>[number]>['continueWatching']
>

export async function getContinueWatching({ locale }: { locale: string }): Promise<ContinueWatchingProject[]> {
  const response = await getWebClient({ locale }).query<GetContinueWatchingQuery>({
    query: GET_CONTINUE_WATCHING_DATA,
    errorPolicy: 'all',
  })
  return response.data.projects?.filter(isDefined) ?? []
}

export function mapContinueWatchingProjectsToContentLinks(
  projects: ContinueWatchingProject[],
): ContinueWatchingProject[] {
  return projects.filter((project) => isDefined(project.continueWatching?.watchPosition))
}

export function isContinueWatchingProject(continueWatching: unknown): continueWatching is ContinueWatchingProject {
  const continueWatchingProject = continueWatching as ContinueWatchingProject

  return (
    continueWatchingProject &&
    Boolean(continueWatchingProject.slug) &&
    Boolean(continueWatchingProject.continueWatching)
  )
}

export function isContinueWatchingEpisode(continueWatching: unknown): continueWatching is ContinueWatchingEpisode {
  const continueWatchingEpisode = continueWatching as ContinueWatchingEpisode

  return (
    continueWatchingEpisode && Boolean(continueWatchingEpisode.guid) && Boolean(continueWatchingEpisode?.watchPosition)
  )
}

export function isResumeWatchingEdge(edge: ResumeWatchingEdgeRaw): edge is ResumeWatchingEdge {
  const typename = edge.node.content?.__typename
  return typename === 'ContentEpisode' || typename === 'ContentMovie' || typename === 'ContentSpecial'
}
